import {
	IconArrowUpRight,
	IconBrandInstagram,
	IconBrandLinkedin,
	IconCheck,
	IconFlame,
	IconLoader2,
	IconPhoto,
	IconSpeakerphone,
	IconWorld,
	IconX,
} from "@tabler/icons-react"
import { type FC, useEffect } from "react"

import { type BlogPost } from "@forento/core/models/dashboard"
import { cn } from "@forento/ui/utilities/tailwind"

import { useToast } from "~/contexts/ToastContext"
import { useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

import forentoMark from "~/assets/logos/forento-mark.svg"

import { getCreatorHighlightsFormUrl, trackCreatorHighlightsFormOpened } from "./creatorHighlightsForm"

const creatorHighlightsLink = "https://forento.link/dash-as-creatorhighlights"

const benefits = [
	{ label: "Featured on our blog, website, and social media" },
	{ label: "Reach an audience across 150+ countries" },
	{ label: "Answer 7 short questions. We write the story for you" },
	{ label: "Free, and takes about 5 minutes", isEmphasized: true },
]

type Props = { latestHighlight: BlogPost | null }
const CreatorHighlightsBanner: FC<Props> = ({ latestHighlight }) => {
	const user = useUser()
	const toast = useToast()
	const dismissBanner = query.platform.dismissCreatorHighlightsPopup.useMutation({
		async onSuccess() {
			await user.reload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to dismiss the banner. Please try again.")
		},
	})

	useEffect(() => {
		window.posthog?.capture("creator_highlights_banner_shown")
	}, [])

	return (
		<section className="@container relative overflow-hidden rounded-2xl bg-linear-to-br from-orange-500 via-rose-400 to-fuchsia-600 text-white shadow-lg">
			<button
				type="button"
				onClick={() => dismissBanner.mutate()}
				disabled={dismissBanner.isPending}
				aria-label="Dismiss Creator Highlights banner"
				className="absolute top-4 right-4 z-10 flex size-8 cursor-pointer items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/15 hover:text-white disabled:cursor-auto disabled:opacity-50"
			>
				{dismissBanner.isPending ? (
					<IconLoader2 className="size-4 animate-spin" />
				) : (
					<IconX className="size-5" />
				)}
			</button>
			<div className="flex flex-col gap-8 p-6 @3xl:flex-row @3xl:items-center @3xl:gap-10 @3xl:p-7">
				<div className="flex min-w-0 flex-1 flex-col items-start">
					<div className="mb-4 flex items-center gap-3">
						<div className="flex size-9 items-center justify-center rounded-lg bg-white/20">
							<IconSpeakerphone className="size-5" stroke={2.5} />
						</div>
						<span className="flex items-center gap-2 rounded-full bg-white/20 px-3 py-1.5 text-sm font-medium">
							<IconFlame className="size-4" />
							Limited spots
						</span>
					</div>
					<h2 className="max-w-xl pr-8 text-xl leading-tight font-semibold text-balance sm:text-2xl">
						Get your academy featured. Free.
					</h2>
					<p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/90 sm:text-[15px]">
						Creator Highlights is our series spotlighting Forento academies across our blog, website, and
						social. Thousands of creators and students from 150+ countries visit our channels. This is your
						chance to get in front of them.
					</p>
					<ul className="mt-4 flex flex-col gap-2 text-sm sm:text-[15px]">
						{benefits.map(benefit => (
							<li
								key={benefit.label}
								className={cn("flex gap-3", benefit.isEmphasized && "font-semibold")}
							>
								<span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-white/25">
									<IconCheck className="size-3.5" stroke={3} />
								</span>
								<span>{benefit.label}</span>
							</li>
						))}
					</ul>
					<div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
						<a
							href={user.user ? getCreatorHighlightsFormUrl(user.user, "banner") : undefined}
							target="_blank"
							rel="noreferrer noopener"
							onClick={() => {
								trackCreatorHighlightsFormOpened("banner")
								dismissBanner.mutate()
							}}
							className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-white px-5 py-3 text-sm font-semibold text-fuchsia-700 shadow-md transition-colors hover:bg-fuchsia-50"
						>
							Share your story
						</a>
						<a
							href={creatorHighlightsLink}
							target="_blank"
							rel="noreferrer noopener"
							className="inline-flex cursor-pointer items-center gap-1 text-sm font-semibold text-white underline underline-offset-4 hover:text-white/80"
						>
							See earlier creator stories
							<IconArrowUpRight className="size-4" />
						</a>
					</div>
				</div>
				<HighlightPreview post={latestHighlight} />
			</div>
		</section>
	)
}

const HighlightPreview: FC<{ post: BlogPost | null }> = ({ post }) => (
	<div
		aria-hidden
		className="mr-4 hidden w-[280px] shrink-0 rotate-2 rounded-2xl bg-white p-4 text-gray-900 shadow-xl @3xl:block"
	>
		<div className="flex items-center gap-3">
			<div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-violet-50">
				<img src={forentoMark} alt="" className="size-6" />
			</div>
			<div className="min-w-0">
				<p className="text-[15px] leading-tight font-semibold">Forento</p>
				<p className="text-sm text-gray-500">Creator Highlights</p>
			</div>
		</div>
		<p className="mt-3 line-clamp-3 text-[15px] leading-snug">
			{post?.title ?? "See how your academy helps students grow, told in your own words"}
		</p>
		<div className="mt-3 flex aspect-video items-center justify-center overflow-hidden rounded-xl bg-linear-to-br from-orange-100 to-pink-100">
			{post?.thumbnailFilePath ? (
				<img src={post.thumbnailFilePath} alt="" className="size-full object-cover" />
			) : (
				<IconPhoto className="size-8 text-pink-300" />
			)}
		</div>
		<div className="mt-3 flex items-center gap-3 border-t border-gray-100 pt-3 text-gray-400">
			<IconWorld className="size-5" />
			<IconBrandLinkedin className="size-5" />
			<IconBrandInstagram className="size-5" />
			<span className="ml-auto text-sm font-semibold text-emerald-600">Featured</span>
		</div>
	</div>
)

export default CreatorHighlightsBanner
